// PopUpSummary.jsx
import React from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  ListItem,
  ListItemAvatar,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  PopUpSummaryRoot,
  PopUpSummaryHeader,
  PopUpSummaryContent,
  PopUpSummaryList,
} from "./PopUpSummary.styles";
import { ShareConfigCode } from "../ShareConfigCode";
import CloseIcon from "@mui/icons-material/Close";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const PopUpSummary = ({ onHandleClose }) => {
  const { items, subtotal } = useSelectedOptions();
  const theme = useTheme();

  const filteredItems = items.filter(
    (item) =>
      (item?.inSummary !== false || item?.inSummary !== null) && item?.price > 0
  );

  return (
    <PopUpSummaryRoot>
      <Button
        variant="text"
        sx={{
          position: "fixed",
          zIndex: 9,
          top: 0,
          right: { xs: "-10px", md: "-5px" },
          color: theme?.palette?.companyColors?.secondary,
          fontSize: { xs: "1.75rem", lg: "2.25rem" },
          transition: "all 0.3s ease-in-out",
        }}
        onClick={() => {
          onHandleClose(false, "click");
        }}
      >
        <CloseIcon
          sx={{
            padding: "3px 0 0",
            fontSize: "3.5rem",
          }}
        />
      </Button>
      <PopUpSummaryHeader>
        <Typography variant="h4" sx={{ fontWeight: 400, flex: 1 }}>
          Summary of your selected options
        </Typography>
      </PopUpSummaryHeader>
      <PopUpSummaryContent dense>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            flex: 1.3,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            padding: { xs: "1rem 0", lg: "1rem 2rem 1rem 0" },
          }}
        >
          <PopUpSummaryList>
            {filteredItems.map((it, indexIt) => {
              return (
                <React.Fragment key={it?.groupKey}>
                  <ListItem>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flex: 1,
                        padding: "0.25rem 0.5rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          textAlign: "right",
                          flex: 1,
                        }}
                      >
                        {it?.image ? (
                          <ListItemAvatar>
                            <Avatar
                              variant="square"
                              src={it?.image}
                              alt={it?.name}
                              sx={{
                                borderRadius: theme?.borderRadius?.small,
                                boxShadow: "0 0 2px 0 rgba(0,0,0,0.25)",
                              }}
                            />
                          </ListItemAvatar>
                        ) : null}
                        {it?.subCategory && (
                          <>
                            {it?.name === "Base price" ? (
                              <Typography
                                variant="body1"
                                sx={{ fontSize: "1.4rem", textAlign: "left" }}
                              >
                                {it?.name}
                              </Typography>
                            ) : (
                              <Typography
                                variant="body1"
                                sx={{ fontSize: "1.4rem", textAlign: "left" }}
                              >
                                {it?.subCategory}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          justifyContent: "flex-end",
                          flex: 1,
                          margin: it?.name ? "5px 0 -1px" : 0,
                        }}
                      >
                        {it?.name && it?.name !== "Base price" && (
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              lineHeight: 1,
                              fontSize: "1.35em",
                              textAlign: "right",
                              color: theme?.palette?.companyColors?.greyLight1,
                            }}
                          >
                            {it?.name}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: {
                              xs: "1.1rem",
                              md: "1.3rem",
                            },
                            color: theme?.palette?.companyColors?.secondary,
                          }}
                        >
                          {currency.format(Number(it?.price) || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                  {filteredItems.length !== indexIt + 1 && (
                    <Divider
                      component="li"
                      sx={{
                        width: "100%",
                        margin: "auto",
                        borderColor: theme.palette.companyColors.greyLight2,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </PopUpSummaryList>
          <Box
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: { xs: "center", lg: "flex-end" },
              gap: "10px",
              mt: "20px",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 500 }}>
              Total:
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: "-1px",
                fontSize: { xs: "2.5rem", md: "3rem", lg: "3.5rem" },
                lineHeight: 1,
              }}
            >
              {currency.format(subtotal)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            position: "relative",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            width: "100%",
            gap: "4rem",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 400,
                flex: 1,
                fontSize: { xs: "1.6rem", lg: "1.4rem" },
                mb: "1rem",
              }}
            >
              Copy the link to your clipboard or share it directly.
            </Typography>
            <ShareConfigCode />
          </Box>
        </Box>
      </PopUpSummaryContent>
    </PopUpSummaryRoot>
  );
};
